import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { plants as fallbackPlants } from '../data/plants'
import { supabase } from '../lib/supabase'
import type {
  CareLevel,
  Plant,
  PlantHeight,
  PlantRole,
  RegionCode,
  SunExposure,
  WaterNeed,
  WindTolerance,
} from '../types'

type PlantReviewStatus = 'verified' | 'partial'

interface SupabasePlantRow {
  slug: string
  common_name: string
  botanical_name: string
  plant_type: string
  sunlight: 'full_sun' | 'partial' | 'shade'
  water_frequency: WaterNeed
  min_container_diameter_inches: number
  min_container_depth_inches: number
  size_category: 'small' | 'medium' | 'large'
  bloom_time: string[] | null
  native_states: string[] | null
  ecological_benefits: string[] | null
  container_notes: string | null
  review_status: PlantReviewStatus
  source_notes: string | null
  toxic_to: string[] | null
  toxicity_notes: string | null
  growth_habit: string | null
  container_suitability: Plant['containerSuitability']
  container_role: string | null
  mixed_container_suitability: string | null
  crowding_guidance: string | null
  depth_guidance: string | null
}

export interface PlantFilters {
  roles?: string[]
  sunlight?: string[]
  water?: string[]
  colors?: string[]
  pestResistance?: string[]
  onlyNative?: boolean
  onlySafePets?: boolean
  onlySafeChildren?: boolean
  onlyPerennial?: boolean
  windTolerance?: string[]
  minContainerSize?: string
  searchText?: string
  region?: RegionCode | null
}

interface PlantCatalogValue {
  plants: Plant[]
  getPlantById: (id: string) => Plant | undefined
  filterPlants: (hardinessZone: number | null, filters: PlantFilters) => Plant[]
  isLoading: boolean
  error: string | null
  source: 'supabase' | 'fallback'
}

const PlantCatalogContext = createContext<PlantCatalogValue | null>(null)

const SUN_MAP: Record<SupabasePlantRow['sunlight'], SunExposure> = {
  full_sun: 'full-sun',
  partial: 'partial-shade',
  shade: 'full-shade',
}

const TYPE_ROLE_MAP: Record<string, PlantRole[]> = {
  herb: ['filler'],
  vegetable: ['filler'],
  flower: ['filler'],
  native_wildflower: ['filler'],
  ornamental_grass: ['thriller'],
  shrub: ['thriller'],
  vine: ['spiller'],
  tree: ['thriller'],
}

const TYPE_EMOJI_MAP: Record<string, string> = {
  herb: '🌿',
  vegetable: '🥬',
  flower: '🌸',
  native_wildflower: '🌼',
  ornamental_grass: '🌾',
  shrub: '🌳',
  vine: '🍃',
  tree: '🌲',
}

const TYPE_COLOR_MAP: Record<string, string> = {
  herb: '#7fb069',
  vegetable: '#5f8f4e',
  flower: '#d989b5',
  native_wildflower: '#f2b84b',
  ornamental_grass: '#c7a76c',
  shrub: '#6f9b7b',
  vine: '#7aa874',
  tree: '#5f8d70',
}

export function PlantCatalogProvider({ children }: { children: ReactNode }) {
  const [remotePlants, setRemotePlants] = useState<Plant[] | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true

    async function loadPlants() {
      setIsLoading(true)
      setError(null)

      const { data, error: queryError } = await supabase
        .from('plants')
        .select(`
          slug,
          common_name,
          botanical_name,
          plant_type,
          sunlight,
          water_frequency,
          min_container_diameter_inches,
          min_container_depth_inches,
          size_category,
          bloom_time,
          native_states,
          ecological_benefits,
          container_notes,
          review_status,
          source_notes,
          toxic_to,
          toxicity_notes,
          growth_habit,
          container_suitability,
          container_role,
          mixed_container_suitability,
          crowding_guidance,
          depth_guidance
        `)
        .neq('review_status', 'needs_review')
        .order('review_status', { ascending: false })
        .order('common_name', { ascending: true })

      if (!active) return

      if (queryError) {
        setRemotePlants(null)
        setError(queryError.message)
      } else {
        setRemotePlants((data ?? []).map(mapSupabasePlant))
      }

      setIsLoading(false)
    }

    loadPlants()

    return () => {
      active = false
    }
  }, [])

  const plants = remotePlants?.length ? remotePlants : fallbackPlants

  const value = useMemo<PlantCatalogValue>(() => {
    const byId = new Map(plants.map(plant => [plant.id, plant]))

    return {
      plants,
      getPlantById: (id: string) => byId.get(id) ?? fallbackPlants.find(plant => plant.id === id),
      filterPlants: (hardinessZone, filters) => filterCatalogPlants(plants, hardinessZone, filters),
      isLoading,
      error,
      source: remotePlants?.length ? 'supabase' : 'fallback',
    }
  }, [error, isLoading, plants, remotePlants?.length])

  return <PlantCatalogContext.Provider value={value}>{children}</PlantCatalogContext.Provider>
}

export function usePlantCatalog() {
  const ctx = useContext(PlantCatalogContext)
  if (!ctx) throw new Error('usePlantCatalog must be used inside PlantCatalogProvider')
  return ctx
}

function mapSupabasePlant(row: SupabasePlantRow): Plant {
  const role = mapRole(row)
  const nativeStates = row.native_states ?? []
  const toxicTo = row.toxic_to ?? []
  const ecologicalBenefits = row.ecological_benefits ?? []
  const descriptionParts = [
    row.container_notes,
    ecologicalBenefits.length ? `Supports ${ecologicalBenefits.join(', ')}.` : null,
    row.toxicity_notes ? `Safety note: ${row.toxicity_notes}` : null,
  ].filter(Boolean)

  return {
    id: row.slug,
    name: row.common_name,
    scientificName: row.botanical_name,
    emoji: TYPE_EMOJI_MAP[row.plant_type] ?? '🌱',
    accentColor: TYPE_COLOR_MAP[row.plant_type] ?? '#7fb069',
    role,
    hardiness: { min: 4, max: 10 },
    sunlight: [SUN_MAP[row.sunlight] ?? 'partial-shade'],
    water: row.water_frequency,
    minContainerSize: row.size_category,
    colors: inferColors(row.plant_type),
    bloomMonths: mapBloomMonths(row.bloom_time ?? []),
    pestResistance: 'moderate',
    nativeRegions: nativeStates,
    toxicity: {
      pets: toxicTo.includes('cats') || toxicTo.includes('dogs'),
      children: toxicTo.includes('children'),
    },
    windTolerance: inferWindTolerance(row.growth_habit),
    isPerennial: row.plant_type !== 'vegetable' && row.plant_type !== 'herb',
    overwinteringNote: row.depth_guidance ?? 'Use conservative winter care for containers; roots are more exposed in pots than in-ground plantings.',
    wateringFrequency: mapWateringFrequency(row.water_frequency),
    fertilizingFrequency: 'Use a balanced container fertilizer during active growth.',
    description: descriptionParts.join(' ') || 'Container guidance is being curated from source-backed plant data.',
    careLevel: inferCareLevel(row.container_suitability),
    height: inferHeight(row.size_category),
    tags: [
      row.plant_type.replace(/_/g, '-'),
      row.review_status,
      nativeStates.length ? 'native' : '',
      ...ecologicalBenefits,
    ].filter(Boolean),
    reviewStatus: row.review_status,
    sourceNotes: row.source_notes,
    ecologicalBenefits,
    crowdingGuidance: row.crowding_guidance,
    depthGuidance: row.depth_guidance,
    containerSuitability: row.container_suitability,
  }
}

function mapRole(row: SupabasePlantRow): PlantRole[] {
  if (row.container_role && row.container_role !== 'standalone') return [row.container_role as PlantRole]
  if (row.mixed_container_suitability === 'standalone') return ['thriller']
  return TYPE_ROLE_MAP[row.plant_type] ?? ['filler']
}

function inferColors(plantType: string): string[] {
  if (plantType === 'ornamental_grass') return ['green', 'silver']
  if (plantType === 'herb' || plantType === 'vegetable') return ['green']
  return ['yellow', 'purple', 'pink', 'white']
}

function mapBloomMonths(seasons: string[]): number[] {
  const months = new Set<number>()
  for (const season of seasons) {
    if (season === 'spring') [3, 4, 5].forEach(month => months.add(month))
    if (season === 'summer') [6, 7, 8].forEach(month => months.add(month))
    if (season === 'fall') [9, 10, 11].forEach(month => months.add(month))
    if (season === 'winter') [12, 1, 2].forEach(month => months.add(month))
  }
  return [...months].sort((a, b) => a - b)
}

function inferWindTolerance(growthHabit: string | null): WindTolerance {
  if (growthHabit === 'climbing' || growthHabit === 'vining') return 'low'
  if (growthHabit === 'upright') return 'moderate'
  return 'high'
}

function inferCareLevel(containerSuitability: Plant['containerSuitability']): CareLevel {
  if (containerSuitability === 'container_friendly') return 'easy'
  if (containerSuitability === 'not_recommended') return 'difficult'
  return 'moderate'
}

function inferHeight(sizeCategory: string): PlantHeight {
  if (sizeCategory === 'large') return 'tall'
  if (sizeCategory === 'medium') return 'medium'
  return 'low'
}

function mapWateringFrequency(water: WaterNeed): string {
  if (water === 'low') return 'Let the top inch of mix dry before watering.'
  if (water === 'high') return 'Keep container mix consistently moist, with drainage.'
  return 'Water when the top inch of mix is dry.'
}

function filterCatalogPlants(plants: Plant[], hardinessZone: number | null, filters: PlantFilters): Plant[] {
  return plants.filter(plant => {
    if (hardinessZone !== null) {
      if (plant.hardiness.min > hardinessZone || plant.hardiness.max < hardinessZone) return false
    }
    if (filters.roles?.length && !filters.roles.some(r => plant.role.includes(r as PlantRole))) return false
    if (filters.sunlight?.length && !filters.sunlight.some(s => plant.sunlight.includes(s as SunExposure))) return false
    if (filters.water?.length && !filters.water.includes(plant.water)) return false
    if (filters.colors?.length && !filters.colors.some(c => plant.colors.includes(c))) return false
    if (filters.pestResistance?.length && !filters.pestResistance.includes(plant.pestResistance)) return false
    if (filters.onlyNative) {
      if (filters.region && !plant.nativeRegions.includes(filters.region)) return false
      if (!filters.region && plant.nativeRegions.length === 0) return false
    }
    if (filters.onlySafePets && plant.toxicity.pets) return false
    if (filters.onlySafeChildren && plant.toxicity.children) return false
    if (filters.onlyPerennial && !plant.isPerennial) return false
    if (filters.windTolerance?.length && !filters.windTolerance.includes(plant.windTolerance)) return false
    if (filters.minContainerSize) {
      const sizeOrder = ['small', 'medium', 'large']
      if (sizeOrder.indexOf(plant.minContainerSize) > sizeOrder.indexOf(filters.minContainerSize)) return false
    }
    if (filters.searchText) {
      const q = filters.searchText.toLowerCase()
      const searchable = [
        plant.name,
        plant.scientificName,
        plant.description,
        plant.ecologicalBenefits?.join(' '),
      ].join(' ').toLowerCase()
      if (!searchable.includes(q)) return false
    }
    return true
  }).sort((a, b) => {
    const aNative = filters.region ? a.nativeRegions.includes(filters.region) : a.nativeRegions.length > 0
    const bNative = filters.region ? b.nativeRegions.includes(filters.region) : b.nativeRegions.length > 0
    if (aNative !== bNative) return aNative ? -1 : 1
    if (a.reviewStatus !== b.reviewStatus) return a.reviewStatus === 'verified' ? -1 : 1
    return a.name.localeCompare(b.name)
  })
}
